/**
 * Ink strokes (pure, unit tested): pressure simulation for fingers, perfect-freehand outlines as SVG
 * path data (page units), and conversion of a captured stroke to quantised, simplified (x, y, p)
 * triplets for the annotation model.
 */
import { getStroke, type StrokeOptions } from 'perfect-freehand';
import { Q, quant, simplifyInk } from '../../shared/annotations';
import { UNITS_W } from './geometry';

/** A captured point: normalised page coordinates + pressure 0..1. */
export interface RawPt {
  x: number;
  y: number;
  p: number;
}

/** Raw points kept per stroke before simplification (memory guard). */
export const MAX_RAW_POINTS = 8000;
export const FINGER_START_PRESSURE = 0.5;
const PRESSURE_RATE = 0.275;

/**
 * Velocity-based pressure for fingers / mice (like perfect-freehand's simulation, but computed once
 * at capture time so the stored stroke renders identically everywhere): fast movement → thinner.
 * `dist` and `size` in the same units (page units).
 */
export function simulatePressure(prev: number, dist: number, size: number): number {
  const sp = Math.min(1, dist / Math.max(0.0001, size));
  const rp = Math.min(1, 1 - sp);
  const next = prev + (rp - prev) * (sp * PRESSURE_RATE);
  return Math.max(0.05, Math.min(1, next));
}

/** Pen pressure from a pointer event value (some styluses report 0 while touching). */
export function penPressure(pressure: number | undefined): number {
  return typeof pressure === 'number' && pressure > 0 ? Math.min(1, pressure) : 0.5;
}

export function strokeOptions(size: number, last: boolean): StrokeOptions {
  return {
    size,
    thinning: 0.55,
    smoothing: 0.5,
    streamline: 0.4,
    simulatePressure: false,
    start: { cap: true, taper: false },
    end: { cap: true, taper: false },
    last,
  };
}

const r2 = (v: number): string => (Math.round(v * 100) / 100).toString();

/** perfect-freehand outline polygon → smooth closed SVG path (quadratic midpoints). */
export function svgPathFromOutline(outline: readonly (readonly number[])[]): string {
  const n = outline.length;
  if (n < 4) return '';
  const avg = (a: number, b: number) => (a + b) / 2;
  const p0 = outline[0]!;
  const p1 = outline[1]!;
  const p2 = outline[2]!;
  let d = `M${r2(p0[0]!)},${r2(p0[1]!)} Q${r2(p1[0]!)},${r2(p1[1]!)} ${r2(avg(p1[0]!, p2[0]!))},${r2(avg(p1[1]!, p2[1]!))} T`;
  for (let i = 2; i < n - 1; i++) {
    const a = outline[i]!;
    const b = outline[i + 1]!;
    d += `${r2(avg(a[0]!, b[0]!))},${r2(avg(a[1]!, b[1]!))} `;
  }
  return `${d}Z`;
}

/** A filled circle path (fallback for single-point strokes). */
export function dotPath(x: number, y: number, r: number): string {
  return `M${r2(x - r)},${r2(y)}a${r2(r)},${r2(r)} 0 1,0 ${r2(2 * r)},0a${r2(r)},${r2(r)} 0 1,0 ${r2(-2 * r)},0Z`;
}

/** Outline path of [x, y, pressure] points in page units; `size` = nominal diameter in page units. */
export function outlinePath(points: readonly (readonly number[])[], size: number, last: boolean): string {
  if (points.length === 0) return '';
  const outline = getStroke(points as number[][], strokeOptions(size, last));
  const d = svgPathFromOutline(outline);
  if (d) return d;
  const p = points[0]!;
  return dotPath(p[0]!, p[1]!, Math.max(0.5, (size / 2) * (0.5 + (p[2] ?? 0.5))));
}

/** Stored triplets (quantised) → [x, y, pressure] in page units for a page `H` units high. */
export function inkPointsUnits(pts: readonly number[], H: number): number[][] {
  const out: number[][] = [];
  for (let i = 0; i + 2 < pts.length; i += 3) out.push([(pts[i]! / Q) * UNITS_W, (pts[i + 1]! / Q) * H, pts[i + 2]! / 1000]);
  return out;
}

/** Captured stroke → quantised flat triplets (consecutive duplicates removed, RDP simplified). */
export function quantiseStroke(points: readonly RawPt[]): number[] {
  const flat: number[] = [];
  let px = -1;
  let py = -1;
  for (const p of points) {
    const x = quant(p.x);
    const y = quant(p.y);
    const pr = Math.max(0, Math.min(1000, Math.round(p.p * 1000)));
    if (x === px && y === py) continue;
    flat.push(x, y, pr);
    px = x;
    py = y;
  }
  return simplifyInk(flat, 5);
}

/* ─── Render cache ──────────────────────────────────────────────────────── */

const pathCache = new Map<string, string>();
const CACHE_MAX = 12_000;

/** Cached outline path of a stored ink object (keyed by id + hlc + page height). */
export function cachedInkPath(key: string, pts: readonly number[], width: number, H: number): string {
  const hit = pathCache.get(key);
  if (hit !== undefined) return hit;
  const d = outlinePath(inkPointsUnits(pts, H), (width / Q) * UNITS_W, true);
  if (pathCache.size >= CACHE_MAX) pathCache.clear();
  pathCache.set(key, d);
  return d;
}
